import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { AgencyTimesheetValueAccessEnum } from '@nxsys/constants/AppEnums';
import {
  useCreateAgencyTimesheetValueById,
  useDeleteAgencyTimesheetValueById,
  useGetAgencyTimesheetValueById,
} from '@nxsys/hooks/api/agency';
import {
  useCreateEmployeeTimesheetValueById,
  useGetEmployeeTimesheetValueById,
} from '@nxsys/hooks/api/employee';
import {
  useCreateCompanyTimesheetValueById,
  useGetCompanyTimesheetValueById,
} from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import AddNewMarginModal, { AddNewMarginFormValues } from './AddNewMargin';
import { toNumber } from 'lodash';
import { CreateAgencyTimesheetValueByIdParams } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import { Icons } from '@nxsys/types/icons';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';

export type MarginTableEntry = {
  fromValue: string;
  toValue: string;
  marginAmount?: string;
  marginPercent?: string;
};

type MarginTableModalProps = {
  title: string;
  isOpen: boolean;
  toggle: () => void;
  agencyId?: string;
  /** Set to open the table for a subcontractor instead of an agency. */
  subcontractorId?: string;
  /** Set to open the company default table instead of an agency's. */
  companyId?: string;
  accessType: AgencyTimesheetValueAccessEnum;
  mode?: 'create' | 'update';
  localData?: MarginTableEntry[];
  onLocalDataChange?: (data: MarginTableEntry[]) => void;
};

const MarginTableModal: React.FC<MarginTableModalProps> = ({
  title,
  isOpen,
  toggle,
  agencyId: agencyIdProps,
  subcontractorId,
  companyId,
  accessType,
  mode = 'update',
  localData = [],
  onLocalDataChange,
}) => {
  const { messages } = useTypedIntl();
  const params = useParams();
  // Agency, subcontractor and company share the same table UI and payload;
  // only the owner the rows hang off differs.
  const owner: 'employee' | 'company' | 'agency' = subcontractorId
    ? 'employee'
    : companyId
      ? 'company'
      : 'agency';
  const ownerId =
    subcontractorId || companyId || params.agencyId || agencyIdProps || '';
  const isCreateMode = mode === 'create';

  const getters = {
    agency: useGetAgencyTimesheetValueById(),
    employee: useGetEmployeeTimesheetValueById(),
    company: useGetCompanyTimesheetValueById(),
  };
  const {
    run: getAgencyTimesheetValueById,
    value: agencyTimesheetValueValue,
    loading: agencyTimesheetValueLoading,
  } = getters[owner];

  const localDataSorted = useMemo(
    () => [...localData].sort((a, b) => toNumber(a.fromValue) - toNumber(b.fromValue)),
    [localData],
  );

  const agencyTimesheetValueData = useMemo(() => {
    if (isCreateMode) return localDataSorted;
    const data = agencyTimesheetValueValue?.data;
    if (!data) return data;
    return [...data].sort(
      (a, b) => toNumber(a.fromValue) - toNumber(b.fromValue),
    );
  }, [isCreateMode, localDataSorted, agencyTimesheetValueValue]);

  useEffect(() => {
    if (isOpen && !isCreateMode && ownerId) handleFetchData();
  }, [isOpen, ownerId, accessType]);

  const handleFetchData = () => {
    if (!ownerId || isCreateMode) return;
    getAgencyTimesheetValueById([
      ownerId,
      {
        page: 0,
        size: 9999,
        access: accessType,
      },
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnsType<any> = [
    {
      key: 'actions',
      title: messages['common.action'],
      align: 'left',
      width: 100,
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => handleConfirmDelete(record)}>
            <Icons.IconDelete />
          </li>
        </ul>
      ),
    },
    {
      key: 'from',
      title: messages['common.from'],
      dataIndex: 'fromValue',
      align: 'right',
    },
    {
      key: 'to',
      title: messages['common.to'],
      dataIndex: 'toValue',
      align: 'right',
    },
    ...((accessType === AgencyTimesheetValueAccessEnum.PERCENT
      ? [
          {
            key: 'marginPercent',
            title: `% ${messages['common.marginAmount']}`,
            dataIndex: 'marginPercent',
            align: 'right',
          },
        ]
      : [
          {
            key: 'marginAmount',
            title: messages['common.marginAmount'],
            dataIndex: 'marginAmount',
            align: 'right',
          },
        ]) as ColumnsType<IAgencyTimesheetValue>),
  ];

  const creators = {
    agency: useCreateAgencyTimesheetValueById(),
    employee: useCreateEmployeeTimesheetValueById(),
    company: useCreateCompanyTimesheetValueById(),
  };
  const {
    run: createAgencyTimesheetValueById,
    loading: createAgencyTimesheetValueByIdLoading,
  } = creators[owner];

  const [addNewMarginModalOpen, setAddNewMarginModalOpen] = useState(false);
  const toggleAddNewMarginModal = () =>
    setAddNewMarginModalOpen(!addNewMarginModalOpen);

  const handleSaveMargin = async (values: AddNewMarginFormValues) => {
    const entry: MarginTableEntry = {
      fromValue: values.from,
      toValue: values.to,
      ...(accessType === AgencyTimesheetValueAccessEnum.PERCENT
        ? { marginPercent: values.margin }
        : { marginAmount: values.margin }),
    };

    if (isCreateMode) {
      onLocalDataChange?.([...localData, entry]);
      toggleAddNewMarginModal();
      return;
    }

    try {
      const params: CreateAgencyTimesheetValueByIdParams = {
        fromValue: entry.fromValue,
        toValue: entry.toValue,
        marginAmount: entry.marginAmount,
        marginPercent: entry.marginPercent,
      };
      await createAgencyTimesheetValueById(ownerId, params);
      showMessage({
        type: 'success',
        content: messages['common.createAgencyTimesheetValueSuccess'],
      });
      handleFetchData();
      toggleAddNewMarginModal();
    } catch (err) {
      console.error(err);
    }
  };

  const { run: deleteAgencyTimesheetValueById } =
    useDeleteAgencyTimesheetValueById();

  const handleConfirmDelete = async (record: IAgencyTimesheetValue | MarginTableEntry) => {
    if (isCreateMode) {
      const updated = localData.filter(
        (item) => !(item.fromValue === record.fromValue && item.toValue === record.toValue),
      );
      onLocalDataChange?.(updated);
      return;
    }

    try {
      AppConfirmationDelete.open({
        title: messages['common.delete'],
        paragraph: messages['common.deleteConfirm'],
        onConfirm: async () => {
          await deleteAgencyTimesheetValueById((record as IAgencyTimesheetValue).id);
          showMessage({
            type: 'success',
            content: messages['common.deleteSuccess'],
          });
          handleFetchData();
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.close'],
          variant: 'outlined',
          onClick: toggle,
        },
      ]}
    >
      <div className="flex justify-end mb-2">
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          onClick={toggleAddNewMarginModal}
          leftIcon={<PlusOutlined />}
        />
      </div>
      <AppTableContainer
        data={agencyTimesheetValueData ?? []}
        loading={isCreateMode ? false : agencyTimesheetValueLoading}
        page={1}
        total={isCreateMode ? localDataSorted.length : (agencyTimesheetValueValue?.totalDocs ?? 0)}
        pageSize={20}
        scroll={{ x: 500, y: 700 }}
        titleEmpty={messages['assignment.empty']}
        columns={columns}
        pagination={false}
      />
      {addNewMarginModalOpen && (
        <AddNewMarginModal
          isOpen={addNewMarginModalOpen}
          toggle={toggleAddNewMarginModal}
          accessType={accessType}
          defaultValues={{
            from:
              agencyTimesheetValueData && agencyTimesheetValueData.length > 0
                ? (
                    Math.max(
                      ...agencyTimesheetValueData.map((item) =>
                        toNumber(item.toValue),
                      ),
                    ) + 0.01
                  ).toString()
                : '',
            to: '',
            margin: '',
          }}
          minFrom={
            agencyTimesheetValueData && agencyTimesheetValueData.length > 0
              ? Math.max(
                  ...agencyTimesheetValueData.map((item) =>
                    toNumber(item.toValue),
                  ),
                )
              : 0
          }
          onSubmit={handleSaveMargin}
          isSubmitting={createAgencyTimesheetValueByIdLoading}
        />
      )}
    </AppModalContainer>
  );
};

export default MarginTableModal;
